/**
 * Utility functions for configuration management
 */

/**
 * Get configuration by name from templatesConfigs array
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @param {string} configName - Name of the configuration to retrieve
 * @param {*} defaultValue - Default value if config not found
 * @returns {*} Configuration value or default
 */
export const getConfigByName = (
  controlledVocab,
  configName,
  defaultValue = {},
) => {
  const templatesConfigs = controlledVocab?.templatesConfigs || [];
  const config = templatesConfigs.find(
    (item) => item.configName === configName,
  );
  return config?.configValue || defaultValue;
};

/**
 * Get authority settings from templatesSettings configuration
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {Object} Authority settings object
 */
export const getAuthoritySettings = (controlledVocab) => {
  const templatesSettings = getConfigByName(
    controlledVocab,
    'templatesSettings',
    {},
  );
  return templatesSettings?.authority || {};
};

/**
 * Check if a field is enabled for authority linking
 * @param {string} fieldPath - The field path to check
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {boolean} True if field is enabled for authority linking
 */
export const isFieldEnabledForAuthority = (fieldPath, controlledVocab) => {
  const authoritySettings = getAuthoritySettings(controlledVocab);
  const enabledFields = authoritySettings?.enabledAuthorityFields || [];
  return enabledFields.some((field) => field.value === fieldPath);
};

/**
 * Get authority source for a specific field type
 * @param {string} fieldPath - The field path
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {string} Authority source ID or 'local' as default
 */
export const getAuthoritySourceForField = (fieldPath, controlledVocab) => {
  const authoritySettings = getAuthoritySettings(controlledVocab);
  const sources = authoritySettings?.sources || {};

  // Map field paths to authority source types
  const fieldToSourceMap = {
    'contributors.name': 'personalNameAuthoritySource',
    'subjects.value': 'topicalSubjectAuthoritySource',
    'series.value': 'uniformTitleAuthoritySource',
    'alternativeTitles.alternativeTitle': 'uniformTitleAuthoritySource',
  };

  const sourceKey = fieldToSourceMap[fieldPath];
  return sourceKey ? sources[sourceKey] || 'local' : 'local';
};

/**
 * Check if auto-create authority is enabled
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {boolean} True if auto-create is enabled
 */
export const isAutoCreateAuthorityEnabled = (controlledVocab) => {
  const authoritySettings = getAuthoritySettings(controlledVocab);
  return authoritySettings?.enableAutoCreateAuthority !== false;
};

/**
 * Check if auto-linking is enabled
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {boolean} True if auto-linking is enabled
 */
export const isAutoLinkingEnabled = (controlledVocab) => {
  const authoritySettings = getAuthoritySettings(controlledVocab);
  return authoritySettings?.enableAutoLinking !== false;
};

/**
 * Get volume management settings from templatesSettings configuration
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {Object} Volume management settings object
 */
export const getVolumeManagementSettings = (controlledVocab) => {
  const templatesSettings = getConfigByName(
    controlledVocab,
    'templatesSettings',
    {},
  );
  return templatesSettings?.volumeManagement || {};
};

/**
 * Check if volume management should be enabled for a specific mode of issuance
 * @param {string} modeOfIssuanceId - The mode of issuance ID to check
 * @param {Object} controlledVocab - The controlled vocabulary object
 * @returns {boolean} True if volume management should be enabled
 */
export const shouldEnableVolumeManagement = (
  modeOfIssuanceId,
  controlledVocab,
) => {
  const volumeSettings = getVolumeManagementSettings(controlledVocab);
  const enabledModes = volumeSettings?.volumeManagementModeOfIssuance || [];

  return (
    volumeSettings?.enableVolumeManagement !== false &&
    enabledModes.some((mode) => mode.value === modeOfIssuanceId)
  );
};
